import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useLazyGetPieceQuery } from '@api/piece';
import { Piece, PieceAction } from '@apiContract';
import { usePiecePdf } from '@components/Mailpiece/hooks/usePiecePdf';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import { SCAN } from 'src/constants';

import { showApiErrorToast } from '@utils/apiErrorToast';

import { useFinishPieceMutation } from '../../../hooks/useFinishPieceMutation';
import { useValidationExitGuard } from '../../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../../index';
import NotifyMairoomComment from '../NotifyComment';
import useNotifyComment from '../NotifyComment/useNotifyComment';
import { DialogOpenAndScan } from './components/DialogOpenAndScan';

export const mailpieceHasContent = (mailpiece: Piece) =>
  Boolean(mailpiece.pieceDoc?.piecePages.length || mailpiece.pieceDoc?.pdfUrl);

export const OpenAndScan = ({ mailpieces, actionStarted, onActionFinish, validationData }: ActionComponentProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const { runAction, isLoading } = useFinishPieceMutation();

  const mailpiece = mailpieces[0];

  const [getPiece] = useLazyGetPieceQuery();
  const { handleCreatePdfFromPages } = usePiecePdf(mailpiece.id, mailpiece.pieceDoc || null);

  const [isPreparing, setIsPreparing] = useState<boolean>(false);
  const [openAndScanOpen, setOpenAndScanOpen] = useState<boolean>(false);
  const [finishOpenAndScanOpen, setFinishOpenAndScanOpen] = useState<boolean>(false);
  const [openAndScanMode, setOpenAndScanMode] = useState<string>(SCAN);

  const { values, changeValues } = useNotifyComment();

  useEffect(() => {
    if (actionStarted) {
      if (openAndScanOpen || finishOpenAndScanOpen) return;
      const fn = !mailpieceHasContent(mailpiece) ? setOpenAndScanOpen : setFinishOpenAndScanOpen;
      fn(true);
    } else {
      setOpenAndScanOpen(false);
      setFinishOpenAndScanOpen(false);
    }
  }, [actionStarted, mailpiece, openAndScanOpen, finishOpenAndScanOpen]);

  const handleOpenAndScanComplete = (mode: string) => {
    setOpenAndScanMode(mode);
    setOpenAndScanOpen(false);
    setFinishOpenAndScanOpen(true);
  };

  const confirmFinishOpenAndScan = async () => {
    try {
      setIsPreparing(true);

      let latestPiece = mailpiece;

      if (!mailpiece.pieceDoc || !mailpiece.pieceDoc.piecePages.length) {
        const latestPieceResult = await getPiece(mailpiece.id);
        latestPiece = latestPieceResult.data || mailpiece;
      }

      if (latestPiece.pieceDoc && latestPiece.pieceDoc.piecePages.length > 0 && !latestPiece.pieceDoc.pdfUrl) {
        await handleCreatePdfFromPages(latestPiece.pieceDoc);
      }

      await runAction({
        action: PieceAction.FinishOpenAndScan,
        pieces: [mailpiece],
        comment: values.comment,
        notifyMailroom: values.notifyMailroom,
        skipNotifyRecipient: values.skipNotifyRecipient,
        validationData,
      });

      onActionFinish();
    } catch (error) {
      showApiErrorToast(error);
    } finally {
      setIsPreparing(false);
    }
  };

  const isScan = openAndScanMode === SCAN;

  return (
    <>
      <DialogOpenAndScan
        mailpieces={mailpieces}
        actionStarted={openAndScanOpen}
        onActionFinish={guardedExit}
        onOpenAndScanComplete={handleOpenAndScanComplete}
      />

      <DialogConfirmation
        open={finishOpenAndScanOpen}
        loading={isPreparing || isLoading}
        maxWidth="sm"
        title={isScan ? 'Convert and send item contents' : 'Send item contents'}
        content={
          <Box>
            <Typography variant="body1" color="text.secondary">
              {isScan
                ? 'Would you like to convert this item’s contents to a PDF and send it to the recipient?'
                : 'Would you like to send this item’s contents to the recipient?'}
            </Typography>

            <NotifyMairoomComment values={values} onChange={changeValues} />
          </Box>
        }
        acceptButtonHandleClick={confirmFinishOpenAndScan}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle={isScan ? 'Convert & Send' : 'Send'}
        cancelButtonTitle={isScan ? 'No, Review Contents' : 'No, Not Now'}
      />

      {exitConfirmDialog}
    </>
  );
};
